import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { SigninRedirectCallbackComponent } from './Modules/Home/signin-redirect-callback.component';
import { SignoutRedirectCallbackComponent } from './Modules/Home/signout-redirect.callback-component';

import { environment } from 'src/environments/environment';
import { AuthGuard } from './shared/guards/auth.guard';
import { PermissionGuard } from './shared/guards/permission.guard';
import { systemAdminGuard } from './shared/guards/system-admin.guard';
import { PermissionConstants as Permissions } from 'src/app/constants/permissions';
import { ExampleComponent } from './shared/styles/example-component';
import { PrivacyComponent } from './core/privacy/privacy.component';
import { HomeComponent } from './Modules/Home/home.component';
import { KIOSK_ADMIN_ROUTES } from './Modules/kiosk-admin/kiosk-admin.routes';


// Exported so a test can verify every PermissionGuard route also carries AuthGuard — see
// permission-guard-route-config.spec.ts. PermissionGuard treats "not signed in" as "defer to
// AuthGuard" rather than a real denial, so a route that skips AuthGuard here would be wide open
// to anonymous users instead of merely racing a redirect.
export const routes: Routes = [
  ...KIOSK_ADMIN_ROUTES,
  // Dev-only preview of the hour entry card fixtures (no session, no API); absent from production builds.
  ...(environment.production ? [] : [{
    path: 'worktime/__entry-card-preview',
    loadComponent: () => import('./Modules/worktime/entry-card/dev-preview/entry-card-preview.component').then(m => m.EntryCardPreviewComponent),
  }, {
    path: 'worktime/__hour-report-preview',
    loadComponent: () => import('./Modules/worktime/entry-card/dev-preview/hour-report-preview.component').then(m => m.HourReportPreviewComponent),
  }]),
  {
    path: 'user-management',
    // canActivate carries PermissionGuard too: canLoad alone only runs on the first lazy-chunk
    // load, so the routes below went unchecked for the rest of the session once loaded.
    canActivate: [AuthGuard, PermissionGuard],
    loadChildren: () =>
      import('./Modules/user-management/user-management.module').then(
        (m) => m.UserManagementModule
      ),
    // AuthGuard paired here too (2026-10-01) — canLoad runs before canActivate ever gets a turn,
    // so without it a signed-out user opening this route cold hit a dead-end "Access Denied"
    // with nothing to ever trigger sign-in.
    canLoad: [AuthGuard, PermissionGuard],
    // Union of every child's requirement — every child now carries its own guard.
    data: {
      requiredPermission: [
        Permissions.UserPermissions.ManageUsers,
        Permissions.UserPermissions.CanSeeProjectUsers,
        Permissions.UserPermissions.CanEditUser,
        Permissions.UserPermissions.CanSeeSensitiveData,
        Permissions.UserPermissions.CanSeeUserDetails,
        Permissions.UserPermissions.CanManageUserRoles,
      ]
    },
  },
  {
    // Järjestelmävalvoja only: the page asks the API who the caller is and the API enforces it on every write.
    path: 'feature-switches',
    canActivate: [AuthGuard, systemAdminGuard],
    loadComponent: () => import('./Modules/feature-switches/feature-switches.component').then((m) => m.FeatureSwitchesComponent),
  },
  {
    path: 'worktypes',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./Modules/work-types/worktype.module').then((m) => m.WorktypeModule),
  },
  {
    path: 'warehouses',
    canActivate: [AuthGuard, PermissionGuard],
    // Union of every child's requirement. A narrower gate here rejected receivers, order
    // makers, pickers and catalogue editors before their own route guard was consulted.
    data: {
      requiredPermission: [
        Permissions.CanManageWarehouses,
        Permissions.CanViewOrders,
        Permissions.CanReceiveDelivery,
        Permissions.CanMakeOrders,
        Permissions.CanPickOrders,
        Permissions.CanAddCategory,
        Permissions.CanEditCategory,
        Permissions.CanAddProduct,
        Permissions.CanEditProduct,
      ]
    },
    loadChildren: () =>
      import('./Modules/Warehouse/warehouse.module').then((m) => m.WarehouseModule),
  },
  {
    path: 'projects',
    canActivate: [AuthGuard, PermissionGuard],
    loadChildren: () =>
      import('./Modules/Projects/project.module').then((m) => m.ProjectModule),
    // Union of every child route's requirement — see projects-routing.module.ts. A narrower
    // gate here rejects users before their own child route is evaluated, which is what
    // stranded reservation/calendar/equipment roles (they hold none of the project trio).
    // The project list and create-project form carry their own guards, so widening this
    // does not expose them.
    data: {
      animation: 'simpleFadeAnimation',
      requiredPermission: [
        Permissions.CanViewProjects,
        Permissions.CanManageProjects,
        Permissions.CanEditProjects,
        // Arrivals
        Permissions.CanViewArrivalsList,
        Permissions.CanManageArrivals,
        Permissions.CanAddArrivals,
        Permissions.CanCreateArrivalReciepts,
        Permissions.CanExportArrivals,
        // Reservations, calendar and equipment
        Permissions.UserPermissions.CanMakeReservations,
        Permissions.UserPermissions.CanSeeCalendar,
        Permissions.CanSeeEquipment,
        // Equipment billing
        Permissions.CanViewEquipmentBilling,
        Permissions.CanAddEquipmentBilling,
        Permissions.CanEditEquipmentBilling,
        // Pass-through billing is not listed: its permissions only add to the project page (2026-10-28).
        // Work types / resources
        Permissions.WorkTypePermissions.CanManageWorkTypes,
        Permissions.WorkTypePermissions.CanAddTypeWorkers,
        // Pace areas, cleaning plan and onboarding
        Permissions.PaceProductionPermissions.CanEditPaceAreas,
        Permissions.CleaningPlanPermissions.CanReadCleaningPlan,
        Permissions.CleaningPlanPermissions.CanEditCleaningPlan,
        Permissions.CleaningPlanPermissions.CanImportCleaningPlan,
        Permissions.CleaningPlanPermissions.CanViewCleaningBilling,
        Permissions.CleaningPlanPermissions.CanViewCleaningBonus,
        Permissions.CleaningPlanPermissions.CanManageCleaningBonus,
        Permissions.CleaningPlanPermissions.CanConfirmCleaningBonus,
        Permissions.OnboardingPermissions.CanManageOnboarding,
      ]
    }
  },
  {
    path: 'overtime',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./Modules/overtime-permissions/overtime-permissions.module').then((m) => m.OvertimePermissionsModule),
  },
  {
    path: 'companies',
    canActivate: [AuthGuard, PermissionGuard],
    data: { requiredPermission: [Permissions.CanManageCompanies, Permissions.CanEditCompanies] },
    loadChildren: () =>
      import('./Modules/companies/company.module').then((m) => m.CompanyModule),
  },
  {
    path: 'billing',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./Modules/billing/billing.module').then(m => m.BillingModule),


  },
  {
    path: 'worktime',
    canActivate: [AuthGuard, PermissionGuard],
    // Union of every child route's requirement — a narrower gate here would
    // reject users before their own child route is ever evaluated.
    data: {
      requiredPermission: [
        Permissions.CanAddHourEntries,
        Permissions.CanManageHours,
        Permissions.CanSeeProjectHours,
        Permissions.CanAcceptEntry,
        Permissions.CanCheckEntry,
        Permissions.CanSeeContractorHours,
        Permissions.SalaryTypePermissions.CanViewSalaryTypes,
        Permissions.SalaryTypePermissions.CanManageSalaryTypes,
        Permissions.SalaryTypePermissions.CanAddSalaryTypes,
        Permissions.SalaryTypePermissions.CanUpdateSalaryTypes,
        Permissions.WorkClassificationPermissions.CanSeeClassificationReports,
        Permissions.WorkClassificationPermissions.CanViewWorkClassification,
        Permissions.WorkClassificationPermissions.CanManageWorkClassification,
        Permissions.WorkLocationPermissions.CanViewWorkLocation,
        Permissions.WorkLocationPermissions.CanManageWorkLocation,
      ]
    },
    loadChildren: () =>
      import('./Modules/worktime/worktime.module').then(
        (m) => m.WorkTimeModule
      ),
  },
  {
    path: 'payroll',
    canActivate: [AuthGuard, PermissionGuard],
    loadComponent: () =>
      import('./Modules/payroll/payroll-export.component').then(
        (m) => m.PayrollExportComponent
      ),
    data: { requiredPermission: [Permissions.CanExportPayroll] },
  },
  { path: 'signin-callback', component: SigninRedirectCallbackComponent },
  { path: 'signout-callback', component: SignoutRedirectCallbackComponent },
  { path: 'home', component: HomeComponent, canActivate: [AuthGuard] },


  {
    path: 'onboarding',
    canActivate: [AuthGuard, PermissionGuard],
    loadChildren: () =>
      import('./Modules/onboarding/onboarding.module').then(
        (m) => m.OnboardingModule
      ),
    // Union of every child's requirement — reports and catalog were entirely unreachable.
    data: {
      requiredPermission: [
        Permissions.OnboardingPermissions.CanViewOnboarding,
        Permissions.OnboardingPermissions.CanManageOnboarding,
        Permissions.OnboardingPermissions.CanSignOnboarding,
        Permissions.OnboardingPermissions.CanEditOnboarding,
        Permissions.OnboardingPermissions.CanAddOnboarding,
        Permissions.OnboardingPermissions.CanViewOnboardingReports,
        Permissions.IsGlobalAdmin,
      ]
    },
  },
  // {path:'pace',
  //   canActivate:[AuthGuard],
  //   loadChildren:()=>
  //     import('./Modules/pace-production/pace-production.module').then(
  //     (m)=>m.PaceProductionModule),
  //     data: { animation: 'PageOne' }
  // },
  {
    path: 'pace',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./Modules/pace-production/pace-production.routes').then(
        m => m.PACE_PRODUCTION_STANDALONE_ROUTES
      ),
  },
  {path:'safety',
    canActivate:[AuthGuard],
    loadChildren:()=>
      import('./Modules/Safety/safety.module').then(
      (m)=>m.SafetyModule),

  },
  {
    path: 'diary',
    canActivate:[AuthGuard, PermissionGuard],
    data: {
      requiredPermission: [
        Permissions.DiaryPermissions.CanViewDiary,
        Permissions.DiaryPermissions.CanAddDiary,
        Permissions.DiaryPermissions.CanEditDiary,
      ]
    },
    loadChildren: () =>
      import('./Modules/diary/diary.module').then((m) => m.DiaryModule),
  },
  {
    path: 'announcements',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./Modules/announcements/announcements.routes').then(
        (m) => m.announcementsRoutes
      ),
  },
  {
    path: 'privacy',
    component: PrivacyComponent,
  },
  {
    path: 'roadmap',
    canActivate: [AuthGuard, PermissionGuard],
    loadComponent: () =>
      import('./Modules/roadmap-tracker/roadmap-tracker.component').then(
        (m) => m.RoadmapTrackerComponent
      ),
    data: { requiredPermission: [Permissions.CanSeeRoadmap] },
  },
  {
    path: 'analytics',
    canActivate: [AuthGuard, PermissionGuard],
    loadChildren: () =>
      import('./Modules/analytics/analytics.routes').then(
        (m) => m.ANALYTICS_ROUTES
      ),
    data: {
      // Union of every child gate — the guard ORs these, so the parent must admit anyone
      // any child admits or they are stranded before their child route is evaluated.
      // '' and events → CanViewAnalytics · audit → CanManageUserRoles
      // · project → CanViewProjectAnalytics
      requiredPermission: [
        Permissions.CanViewAnalytics,
        Permissions.UserPermissions.CanManageUserRoles,
        Permissions.CanViewProjectAnalytics,
      ]
    },
  },
  {
    path: 'server-status',
    canActivate: [AuthGuard, PermissionGuard],
    loadComponent: () =>
      import('./Modules/server-status/server-status-dashboard.component').then(
        (m) => m.ServerStatusDashboardComponent
      ),
    data: { requiredPermission: [Permissions.UserPermissions.CanManageUserRoles] },
  },
  {
    path: 'ai-playground',
    canActivate: [AuthGuard, PermissionGuard],
    loadComponent: () =>
      import('./Modules/ai-playground/ai-playground.component').then(
        (m) => m.AiPlaygroundComponent
      ),
    data: { requiredPermission: [Permissions.CanAccessAIPlayground] },
  },
  {
    // Every signed-in user may see their own record — deliberately no PermissionGuard.
    path: 'omat-tiedot',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./Modules/my-account/my-account.component').then((m) => m.MyAccountComponent),
  },
  {
    path: 'access-tokens',
    canActivate: [AuthGuard, PermissionGuard],
    loadComponent: () =>
      import('./Modules/access-tokens/access-token-list/access-token-list.component').then(
        (m) => m.AccessTokenListComponent
      ),
    data: { requiredPermission: [Permissions.AccessTokenPermissions.CanManageAccessTokens] },
  },
  {
    path: 'ai-assistant',
    canActivate: [AuthGuard, PermissionGuard],
    loadComponent: () =>
      import('./Modules/ai-assistant/connections/ai-assistant-connections.component').then(
        (m) => m.AiAssistantConnectionsComponent
      ),
    title: 'Tekoälyavustaja - RaksaInfoV4®',
    data: { requiredPermission: [Permissions.AiAssistantPermissions.CanConnectAiAssistant, Permissions.AccessTokenPermissions.CanManageAccessTokens] },
  },
  {
    // AuthGuard only, on purpose: a user without the permission must see the explanation on the page, not the no-access page.
    path: 'ai-assistant/connect',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./Modules/ai-assistant/connect/ai-assistant-connect.component').then(
        (m) => m.AiAssistantConnectComponent
      ),
    title: 'Yhdistä tekoälyavustaja - RaksaInfoV4®',
  },
  {
    path: 'access-tokens/admin',
    canActivate: [AuthGuard, PermissionGuard],
    loadComponent: () =>
      import('./Modules/access-tokens/admin/access-token-admin.component').then(
        (m) => m.AccessTokenAdminComponent
      ),
    data: { requiredPermission: [Permissions.IsGlobalAdmin] },
  },
  {
    path: 'manual',
    canActivate: [AuthGuard, PermissionGuard],
    loadChildren: () =>
      import('./Modules/user-manual/user-manual.routes').then(
        (m) => m.USER_MANUAL_ROUTES
      ),
    data: { requiredPermission: [Permissions.UserManualPermissions.CanViewUserManual, Permissions.UserManualPermissions.CanManageUserManual] },
  },
  { path: '', loadComponent: () => import('./Modules/Home/home.component').then(m => m.HomeComponent), title: 'RaksaInfo V4®', canActivate: [AuthGuard], data: { animation: 'simpleFadeAnimation' } },
  {
    // Signed-in users with no role land here. AuthGuard only: such a user holds no permission.
    path: 'awaiting-access',
    loadComponent: () => import('./Modules/Home/awaiting-access.component').then(m => m.AwaitingAccessComponent),
    title: 'RaksaInfo V4®',
    canActivate: [AuthGuard]
  },
  {
    path: 'unauthorized',
    loadComponent: () => import('./Modules/Home/unauthorized.component').then(m => m.UnauthorizedComponent),
    title: 'Access Denied - RaksaInfoV4®'
  },
  {
    // Landing page after /signout-callback completes; deliberately unguarded.
    path: 'logged-out',
    loadComponent: () => import('./Modules/Home/logged-out.component').then(m => m.LoggedOutComponent),
  },
  {
    path: 'session-expired',
    loadComponent: () => import('./Modules/Home/session-expired.component').then(m => m.SessionExpiredComponent),
    title: 'Session Expired - RaksaInfoV4®'
  },
  {path:'layout-examples', component:ExampleComponent},
  {
    path: '**',
    loadComponent: () => import('./core/not-found.component').then(m => m.NotFoundComponent),
    title: '404 Not Found - RaksaInfoV4®'
  },

];

@NgModule({
  imports: [RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
